import { Component, OnInit, ViewChild, TemplateRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { Subject, merge, of as observableOf } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CoreService } from '../../../services/core.service';
import { ManageApplicationNavComponent } from '../manage-application-nav/manage-application-nav.component';
import { SocialAiPlatformFormComponent } from '../../../components/forms/create-account/platform-form/platform-form.component';
import { DialogComponent } from '../../../components/dialog/dialog.component';

@Component({
  selector: 'app-index-platforms',
  standalone: true,
  imports: [
    CommonModule,
    MatPaginatorModule,
    MatTableModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatSortModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatDialogModule,
    ManageApplicationNavComponent,
    SocialAiPlatformFormComponent
  ],
  templateUrl: './index-platforms.component.html',
  styleUrl: './index-platforms.component.scss'
})
export class ManageApplicationIndexPlatformsComponent implements OnInit, OnDestroy {
  @ViewChild('platformFormTmpl', { static: true }) platformFormTmpl!: TemplateRef<any>;
  @ViewChild('confirmDeleteTmpl', { static: true }) confirmDeleteTmpl!: TemplateRef<any>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = ['id', 'platform', 'domain', 'status', 'created_at', 'total_published_posts', 'actions'];

  searchValue: string = "";
  totalItems = 0;
  pageSize = 12;
  currentPage = 1;
  isLoading = true;
  hasLoaded = false;
  selectedPlatform: any = {};

  sortField: string = 'id';
  sortDirection: string = 'desc';

  private destroy$ = new Subject<void>();

  constructor(
    private coreService: CoreService,
    public dialog: MatDialog
  ) { }

  ngOnInit() {
    this.loadPlatforms();
  }

  loadPlatforms(page: number = 1) {
    this.isLoading = true;

    this.coreService
      .getData('/manageAccount/all-platforms/index', {
        page,
        keyword: this.searchValue,
        sort: this.sortField,
        direction: this.sortDirection,
        per_page: this.pageSize
      })
      .subscribe({
        next: (res: any) => {
          this.dataSource.data = res.data || [];
          this.totalItems = res.total || 0;
          this.pageSize = res.per_page || 12;
          this.currentPage = res.current_page || page;
          this.hasLoaded = true;

          if (this.paginator) {
            this.paginator.pageIndex = (res.current_page || 1) - 1;
            this.paginator.length = res.total || 0;
          }
        },
        error: (err) => {
          console.error('Load platforms error:', err);
          this.dataSource.data = [];
          this.hasLoaded = true;
          this.isLoading = false;
        },
        complete: () => {
          this.isLoading = false;
        }
      });
  }

  onSearchSubmit(value: string) {
    this.searchValue = value.trim();
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.loadPlatforms(1);
  }

  clearSearch() {
    this.searchValue = "";
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.loadPlatforms(1);
  }

  onPageChange(event: any) {
    const newPage = event.pageIndex + 1;
    this.currentPage = newPage;
    this.loadPlatforms(newPage);
  }

  onSortChange(sort: Sort) {
    this.sortField = sort.active;
    this.sortDirection = sort.direction || 'desc';
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.loadPlatforms(1);
  }

  addNewPlatform() {
    this.selectedPlatform = {};
    this.dialog.open(DialogComponent, {
      data: {
        content: this.platformFormTmpl,
        header: 'Add Content Platform',
        cls: '!bg-slate-600 !text-white',
      },
      width: '760px',
      maxWidth: '760px',
    });
  }

  editPlatform(row: any) {
    this.selectedPlatform = {
      ...row,
      id: row.id || row.ID
    };

    this.dialog.open(DialogComponent, {
      data: {
        content: this.platformFormTmpl,
        header: 'Edit Platform',
        cls: '!bg-slate-600 !text-white',
      },
      width: '760px',
      maxWidth: '760px',
    });
  }

  openDeleteConfirm(row: any) {
    this.selectedPlatform = row;
    this.dialog.open(DialogComponent, {
      data: {
        content: this.confirmDeleteTmpl,
        header: 'Delete Platform?',
        cls: 'bg-red-500 !text-white',
      },
    });
  }

  deletePlatform() {
    const platformId = this.selectedPlatform.id || this.selectedPlatform.ID;
    if (!platformId) {
      this.coreService.showSnackbar("Platform ID is missing for deletion.");
      return;
    }

    this.coreService
      .deleteData("deletePlatform", String(platformId))
      .subscribe({
        next: () => {
          this.dialog.closeAll();
          this.coreService.showSnackbar('Platform deleted successfully');
          this.loadPlatforms(this.paginator ? this.paginator.pageIndex + 1 : 1);
        },
        error: (err) => {
          console.error('Delete platform error:', err);
          this.dialog.closeAll();
          this.coreService.showSnackbar('Failed to delete platform');
        }
      });
  }

  onSubmitPlatformClbk() {
    this.dialog.closeAll();
    this.loadPlatforms(this.paginator ? this.paginator.pageIndex + 1 : 1);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
